import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ButtonWithTooltip } from 'components/WithTooltip';
import type { WorkOrderPriceItem } from 'gql/graphql';
import { ActKc2StatusEnum, ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { cnMixSpace, IconAlert, IconCopy } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import { useCopyActSkzsPlan } from './hooks/useCopyActSkzsPlan';

interface IActSkzsCellRenderer extends ICellRendererParams<WorkOrderPriceItem> {
    actId: number;
    seasonId: number;
    workOrderStatus: WorkOrderStatusEnum;
    contractStatus: ContractStatusEnum;
    isDisabledByProjectStatus: boolean;
    onSuccess: () => void;
    useAgCellRowDataRefresh: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams: any;
    skzsPanelRefresh: () => void;
}

const requestParams = { isSMRItem: true };

export const ActSkzsCellRenderer = (props: IActSkzsCellRenderer) => {
    const {
        value,
        data,
        actId,
        seasonId,
        workOrderStatus,
        isDisabledByProjectStatus,
        contractStatus,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        skzsPanelRefresh,
        api,
    } = props;
    const { itemId } = data ?? {};

    const isDisabledByWorkOrderStatus =
        workOrderStatus === WorkOrderStatusEnum.Completed || workOrderStatus === WorkOrderStatusEnum.Delayed;

    const currentSeasonData = data?.seasonValues?.find((season) => season.seasonId === seasonId);
    const currentAct = currentSeasonData?.actValues?.find((act) => act.actId === actId);
    const isStatusSkzsUpdated = currentAct?.isSKzSUpdated;
    const isDisabledByActStatus = currentAct?.actStatus !== ActKc2StatusEnum.InProgress;

    const isDisabled = Boolean(
        isDisabledByActStatus ||
            isDisabledByProjectStatus ||
            isDisabledByWorkOrderStatus ||
            contractStatus !== ContractStatusEnum.InImplementation ||
            currentAct?.isSKzSCopyLock,
    );

    const { reloadHandler } = useAgCellRowDataRefresh({
        api,
        requestParams: { ...useAgCellRowDataRefreshParams, ...requestParams },
    });

    const onSuccess = useCallback(() => {
        reloadHandler([itemId]);
        skzsPanelRefresh();
    }, [itemId, reloadHandler, skzsPanelRefresh]);

    const { handleCopyPricePlanValue } = useCopyActSkzsPlan({
        onSuccess,
    });

    const handleClick = useCallback(() => {
        handleCopyPricePlanValue(actId, itemId);
    }, [actId, handleCopyPricePlanValue, itemId]);

    if (value) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {isStatusSkzsUpdated && currentAct?.actStatus !== ActKc2StatusEnum.Accepted && (
                    <ComponentWithTooltip
                        tooltipText="Данные по объему обновлены"
                        className={cnMixSpace({
                            mR: '2xs',
                        })}
                        styles={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <IconAlert size="s" view="warning" />
                    </ComponentWithTooltip>
                )}
                {currentAct?.actStatus === ActKc2StatusEnum.InProgress && (
                    <ButtonWithTooltip
                        buttonProps={{
                            iconLeft: IconCopy,
                            onlyIcon: true,
                            view: 'clear',
                            size: 's',
                            className: cnMixSpace({
                                mR: '2xs',
                            }),
                            onClick: handleClick,
                            disabled: isDisabled,
                        }}
                        tooltipProps={{
                            content: 'Скопировать данные из СКзС',
                        }}
                    />
                )}
                {value}
            </div>
        );
    }

    return value ?? null;
};

ActSkzsCellRenderer.displayName = 'ActSkzsCellRenderer';
